import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAppSelector, type AppDispatch, type RootState } from '@/app/store/store';
import { selectFooterInViewport } from '@/app/store/footerVisibilitySlice';
import {
  clearPlpInitialData,
  selectPlpInitialData,
  selectPlpInitialUrl,
} from '@/features/plp/presentation/store/plpInitialSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePlpCustomerCode } from '@/features/plp/presentation/hooks/usePlpCustomerCode';
import { plpBuild } from '@/features/plp/config/plp.build';
import { filtersBuild } from '@/features/filters/config/filters.build';
import { usePlpListing } from '@/features/plp/presentation/hooks/usePlpListing';
import { usePlpRedirectUrlNavigation } from '@/features/plp/presentation/hooks/usePlpRedirectUrlNavigation';
import { usePlpPincode } from '@/features/plp/presentation/hooks/usePlpPincode';
import { usePlpFilters } from '@/features/filters/presentation/hooks/usePlpFilters';
import { usePlpDesktopFilters } from '@/features/filters/presentation/hooks/usePlpDesktopFilters';
import { usePlpPresentation } from '@/features/plp/presentation/hooks/usePlpPresentation';
import { usePlpAnalytics } from '@/features/plp/presentation/hooks/usePlpAnalytics';
import { useVideoCallCart } from '@/features/video-call/presentation/hooks/useVideoCallCart';
import { useIsDesktop, useIsMobile } from '@/core/hooks/useMediaQuery';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import { clearStoreLocationWidgetSession } from '@/shared/features/physical-store/presentation/hooks/useStoreLocationWidget';
import type { ListingPageResult } from '@/features/plp/domain/models/ProductListing';
import type { GetFiltersParams } from '@/features/filters/application/services/FiltersService';
import type { StoreWidgetData } from '@/features/plp/presentation/components/SelectedStoreWidget';
import {
  getPlpListingKey,
  getPlpBrowseTagsForApi,
  getPlpApiQueryContextFromSearch,
  getPreferredProductsFromSearch,
  getStoreBrowseContextFromUrl,
} from '@/features/plp/utils/plpUrl';
import {
  decodeSidList,
  getCitySidFromCityToStoresMap,
  getSelectedStoreBySid,
  getSelectedStoresBySids,
} from '@/features/plp/utils';
import { PLP_DEFAULT_SORT, PLP_PAGE_TYPE, type PlpSortByValue } from '@/features/plp/constants';

import { PlpBase } from '@/features/plp/presentation/components/PlpBase';
import { PlpSortSheet } from '@/features/plp/presentation/components/PlpSortSheet';
import { PlpFilterSheet } from '@/features/filters/presentation/components/PlpFilterSheet';
import { PlpDesktopFilterSidebar } from '@/features/filters/presentation/components/PlpDesktopFilterSidebar';
import { PincodePopup } from '@/shared/features/pincode/presentation/components/PincodePopup';
import { VideoCallStickyBar } from '@/features/video-call/presentation/components/VideoCallStickyBar';
import { OtherStoresWidget } from '@/features/plp/presentation/components/OtherStoresWidget';
import { StoreFaqWidget } from '@/features/plp/presentation/components/StoreFaqWidget';
import { PlpScrollToTopButton } from '@/shared/widgets/PlpScrollToTopButton';

function getSidFromSearch(search: string): string | undefined {
  const raw = new URLSearchParams(search).get('sid');
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed || undefined;
}

function getSortByFromSearch(search: string): PlpSortByValue {
  const raw = new URLSearchParams(search).get('sortby')?.trim().toLowerCase();
  if (!raw) return PLP_DEFAULT_SORT as PlpSortByValue;
  const exists = (['date_add', 'mostpopular', 'pricelow', 'pricehigh', 'discount'] as PlpSortByValue[]).includes(
    raw as PlpSortByValue,
  );
  return exists ? (raw as PlpSortByValue) : (PLP_DEFAULT_SORT as PlpSortByValue);
}

const EMPTY_LISTING_RESULT: ListingPageResult = {
  header: '',
  totalCount: 0,
  filters: [],
  designs: [],
  exploreLinks: [],
  listingSeo: undefined,
};

export default function StorePlpPage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname || '/stores';

  const sidFromUrl = useMemo(() => getSidFromSearch(location.search), [location.search]);
  const sortBy = useMemo(() => getSortByFromSearch(location.search), [location.search]);
  const preferredProducts = useMemo(() => getPreferredProductsFromSearch(location.search), [location.search]);
  const apiQueryContext = useMemo(() => getPlpApiQueryContextFromSearch(location.search), [location.search]);
  const isFiltered = pathname.includes('/filter/');

  const storeBrowseContext = useMemo(() => getStoreBrowseContextFromUrl(pathname), [pathname]);
  const tags = useMemo(() => getPlpBrowseTagsForApi(pathname), [pathname]);

  // City catalog route: /stores/<city>/catalog.html (sidType=0). A `?sid=` query is often the
  // composite listing sid for every store in that city — not a single-store selection.
  const isCityCatalogRoute = storeBrowseContext.sidType === 0;
  const storeCitySlug = useMemo(() => {
    if (storeBrowseContext.sidType !== 0 || !storeBrowseContext.sidUrl) return undefined;
    try {
      return decodeURIComponent(storeBrowseContext.sidUrl);
    } catch {
      return storeBrowseContext.sidUrl;
    }
  }, [storeBrowseContext.sidType, storeBrowseContext.sidUrl]);

  // When navigating to a city URL via city-sheet, defer the PLP fetch until store selection is applied.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isDeferredCityFetch = Boolean((location.state as any)?.deferPlpFetch);

  const { ref: topSentinelRef, isIntersecting: isTopVisible } = useIntersectionObserver<HTMLDivElement>();
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();
  const footerInViewport = useAppSelector(selectFooterInViewport);
  const shouldPauseInfiniteScroll = useCallback(() => footerInViewport, [footerInViewport]);

  const { service } = useMemo(() => plpBuild(dispatch), [dispatch]);
  const { filtersService } = useMemo(() => filtersBuild(dispatch), [dispatch]);

  const [isSortSheetOpen, setIsSortSheetOpen] = useState(false);
  const [isNoStoreFound, setIsNoStoreFound] = useState(false);
  const [pincodeDescription, setPincodeDescription] = useState<string | undefined>(undefined);

  const {
    pincode,
    inputValue,
    error: pincodeError,
    isPopupOpen,
    openPopup,
    closePopup,
    handleChange,
    handleSubmit,
  } = usePlpPincode();

  useEffect(() => {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    return () => {
      if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
    };
  }, []);

  // Prefetch physical store details early so citySid is available for fetchPage on city pages.
  useEffect(() => {
    service.prefetchPhysicalStoresDetails();
  }, [service]);

  useEffect(() => {
    return () => {
      clearStoreLocationWidgetSession();
    };
  }, []);

  const physicalStoresDetails = useSelector((state: RootState) => service.selectPhysicalStoresDetails(state));
  const cityToStoresMap = physicalStoresDetails?.cityToStoresMap;

  // Derive composite sid for city pages from all stores in that city.
  // Shared with `server/entry-server.tsx` so the SSR listing key and the client listing key match.
  const citySid = useMemo(() => {
    if (!isCityCatalogRoute) return undefined;
    return getCitySidFromCityToStoresMap(cityToStoresMap, storeCitySlug);
  }, [isCityCatalogRoute, cityToStoresMap, storeCitySlug]);

  const isStoreCitySelectionMode = isCityCatalogRoute && isDeferredCityFetch;
  // Block fetch until citySid is resolved on city catalog pages (unless a sid is already in the URL).
  const shouldFetchPlp = !isStoreCitySelectionMode && (!isCityCatalogRoute || citySid != null || sidFromUrl != null);

  const listingSid = sidFromUrl ?? (isCityCatalogRoute ? citySid : storeBrowseContext.sid) ?? apiQueryContext.sid;

  const plpCustomerCode = usePlpCustomerCode();

  const initialPlpData = useSelector(selectPlpInitialData);
  const initialPlpUrl = useSelector(selectPlpInitialUrl);
  const plpKey = useMemo(() => {
    const base = getPlpListingKey(pathname, location.search);
    // Append citySid so the listing key changes when citySid resolves on city pages.
    if (isCityCatalogRoute && citySid) return `${base}::sid=${citySid}`;
    return base;
  }, [pathname, location.search, isCityCatalogRoute, citySid]);

  const ssrInitialDataMatchesRoute =
    initialPlpData != null &&
    initialPlpUrl != null &&
    // Compare against the route URL, not `plpKey`. City pages append `::sid=` to `plpKey` so
    // the listing refetches when composite sid resolves on SPA navigations; that suffix is not
    // part of `__INITIAL_PLP_URL__` and must not discard SSR products/filters on hydration.
    getPlpListingKey(initialPlpUrl) === getPlpListingKey(pathname, location.search);

  const effectiveInitialData =
    sortBy !== PLP_DEFAULT_SORT || !ssrInitialDataMatchesRoute ? null : initialPlpData;

  useEffect(() => {
    if (initialPlpData != null) {
      dispatch(clearPlpInitialData());
    }
    // Run once after first render to drop SSR data so remounts refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPage = useCallback(
    async (pageNumber: number, opts?: { signal?: AbortSignal }): Promise<ListingPageResult> => {
      if (!shouldFetchPlp) {
        return EMPTY_LISTING_RESULT;
      }
      const result = await service.getListing({
        pageNumber,
        tags,
        tags_to_sort_filters: tags,
        sortBy,
        page_type: PLP_PAGE_TYPE.STORE,
        ...(preferredProducts ? { preferredProducts } : {}),
        is_filtered: isFiltered,
        sid: listingSid,
        sidUrl: storeBrowseContext.sidUrl,
        sidType: storeBrowseContext.sidType,
        storeIds: apiQueryContext.storeIds,
        wid: apiQueryContext.wid,
        url_query_string: apiQueryContext.url_query_string,
        design_ids: apiQueryContext.design_ids,
        customer_code: plpCustomerCode,
        ...(opts?.signal ? { signal: opts.signal } : {}),
      });
      return result as unknown as ListingPageResult;
    },
    [
      service,
      tags,
      sortBy,
      preferredProducts,
      isFiltered,
      listingSid,
      storeBrowseContext.sidUrl,
      storeBrowseContext.sidType,
      apiQueryContext,
      shouldFetchPlp,
      plpCustomerCode,
    ],
  );

  const filterParams = useMemo(
    (): Omit<GetFiltersParams, 'tags'> => ({
      page_type: PLP_PAGE_TYPE.STORE,
      tags_to_sort_filters: tags,
      is_filtered: isFiltered,
      is_for_filter_change: false,
      sid: listingSid,
      sidUrl: storeBrowseContext.sidUrl,
      sidType: storeBrowseContext.sidType,
      storeIds: apiQueryContext.storeIds,
      wid: apiQueryContext.wid,
      url_query_string: apiQueryContext.url_query_string,
      design_ids: apiQueryContext.design_ids,
    }),
    [tags, isFiltered, listingSid, storeBrowseContext, apiQueryContext],
  );

  const plp = usePlpListing({
    fetchPage,
    pathname,
    initialData: effectiveInitialData,
    listingKey: plpKey,
    restoreKey: plpKey,
    isMobile,
    shouldPauseInfiniteScroll,
    customerCode: plpCustomerCode,
  });

  // Fix filtersResetUrl for store PLP: the API often returns a non-store URL.
  // Derive the base catalog URL from storeBrowseContext.sidUrl instead.
  // Must be computed after `plp` so listingMeta?.filtersResetUrl is available.
  const fixedFiltersResetUrl = useMemo(() => {
    const raw = plp.listingMeta?.filtersResetUrl;
    if (!storeBrowseContext.sidUrl) return raw;
    const looksLikeNonStoreUrl = !raw || !/^\/stores\//i.test(raw);
    if (!looksLikeNonStoreUrl) return raw;
    let decodedSidUrl: string;
    try {
      decodedSidUrl = decodeURIComponent(storeBrowseContext.sidUrl);
    } catch {
      decodedSidUrl = storeBrowseContext.sidUrl;
    }
    const basePath = `/stores/${decodedSidUrl}/catalog.html`;
    const sidParam = new URLSearchParams(location.search).get('sid');
    return sidParam ? `${basePath}?sid=${encodeURIComponent(sidParam)}` : basePath;
  }, [plp.listingMeta?.filtersResetUrl, storeBrowseContext.sidUrl, location.search]);

  const plpAnalytics = usePlpAnalytics({
    pageType: 'stores',
    listingHeader: plp.listingMeta?.header ?? '',
    collectionTags: tags,
    currentSortBy: sortBy,
    totalCount: plp.listingMeta?.totalCount ?? 0,
  });

  const pageViewFiredRef = useRef<string | null>(null);
  useEffect(() => {
    plpAnalytics.viewedProductIds.clear();
  }, [plpKey, plpAnalytics.viewedProductIds]);

  useEffect(() => {
    if (plp.isLoading) return;
    if (!plp.listingMeta) return;
    if (plp.allDesigns.length === 0) return;
    if (!plpAnalytics.isReady) return;
    if (pageViewFiredRef.current === plpKey) return;
    pageViewFiredRef.current = plpKey;
    plpAnalytics.trackPageView(plp.allDesigns);
  }, [plp.isLoading, plp.listingMeta, plp.allDesigns, plpKey, plpAnalytics]);

  useEffect(() => {
    const handleScroll = () => plpAnalytics.trackScrollDown();
    window.addEventListener('scroll', handleScroll, { passive: true, once: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [plpAnalytics]);

  const applySortToUrl = useCallback(
    (nextSort: PlpSortByValue) => {
      const urlParams = new URLSearchParams(location.search);
      urlParams.set('sortby', nextSort);
      navigate({ pathname: location.pathname, search: `?${urlParams.toString()}` }, { replace: false });
    },
    [location.pathname, location.search, navigate],
  );

  const handleOpenSortSheet = useCallback(() => {
    plpAnalytics.trackSortFilterClicked();
    setIsSortSheetOpen(true);
  }, [plpAnalytics]);

  const handleApplySortFromSheet = useCallback(
    (nextSort: PlpSortByValue) => {
      plpAnalytics.trackSortApply(nextSort);
      applySortToUrl(nextSort);
      setIsSortSheetOpen(false);
      window.scrollTo({ top: 0, behavior: 'auto' });
    },
    [applySortToUrl, plpAnalytics],
  );

  const handleApplySort = useCallback(
    (nextSort: PlpSortByValue) => {
      plpAnalytics.trackSortApply(nextSort);
      applySortToUrl(nextSort);
      window.scrollTo({ top: 0, behavior: 'auto' });
    },
    [applySortToUrl, plpAnalytics],
  );

  const handleOpenBottomPincode = useCallback(() => {
    plpAnalytics.trackPincodeClick(pincode ?? '');
    setPincodeDescription('Browse our exquisite designs: locate stores near you!');
    if (sidFromUrl) {
      openPopup((nextSid) => {
        if (nextSid) {
          const urlParams = new URLSearchParams(location.search);
          urlParams.set('sid', nextSid);
          navigate({ pathname: location.pathname, search: `?${urlParams.toString()}` });
        } else {
          const urlParams = new URLSearchParams(location.search);
          urlParams.delete('sid');
          setIsNoStoreFound(true);
          navigate({ pathname: location.pathname, search: urlParams.toString() ? `?${urlParams.toString()}` : '' });
        }
      });
    } else if (isNoStoreFound) {
      openPopup((nextSid) => {
        if (nextSid) {
          setIsNoStoreFound(false);
          const urlParams = new URLSearchParams(location.search);
          urlParams.set('sid', nextSid);
          navigate({ pathname: location.pathname, search: `?${urlParams.toString()}` });
        } else {
          setIsNoStoreFound(true);
        }
      });
    } else {
      openPopup();
    }
  }, [openPopup, plpAnalytics, pincode, sidFromUrl, isNoStoreFound, location.search, location.pathname, navigate]);

  const handlePincodeSubmit = useCallback(async () => {
    plpAnalytics.trackPincodeEntered(inputValue, !/^[1-9][0-9]{5}$/.test(inputValue.trim()));
    await handleSubmit();
  }, [plpAnalytics, inputValue, handleSubmit]);

  const handlePincodeOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        openPopup();
      } else {
        setPincodeDescription(undefined);
        closePopup();
      }
    },
    [openPopup, closePopup],
  );

  // Resolve selected store data from cityToStoresMap.
  const sidMatches = useMemo(
    () => decodeSidList(sidFromUrl ?? storeBrowseContext.sid),
    [sidFromUrl, storeBrowseContext.sid],
  );
  // City landing UI: city catalog URL with no single-store sid (plain URL or composite city sid).
  const isCityPage = isCityCatalogRoute && sidMatches.length !== 1;
  const isStoreWidgetLoading = cityToStoresMap == null;
  const { selectedStoreData, selectedStoresData } = useMemo(() => {
    if (isCityPage || sidMatches.length === 0) {
      return { selectedStoreData: null, selectedStoresData: [] as StoreWidgetData[] };
    }
    return {
      selectedStoreData: getSelectedStoreBySid<StoreWidgetData>(cityToStoresMap, sidMatches),
      selectedStoresData: getSelectedStoresBySids<StoreWidgetData>(cityToStoresMap, sidMatches),
    };
  }, [cityToStoresMap, sidMatches, isCityPage]);

  const cityDisplayName = useMemo(
    () =>
      selectedStoreData?.cityName?.trim() ||
      (storeCitySlug ? storeCitySlug.split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ') : ''),
    [selectedStoreData, storeCitySlug],
  );
  const storeAreaName = selectedStoreData?.storeName?.trim()?.split(',')[0]?.trim();

  const findLocationFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!plpAnalytics.isReady) return;
    if (plp.isLoading || !plp.listingMeta) return;

    if (isCityPage) {
      // Wait until citySid is resolved so we don't fire on the empty "no designs"
      // placeholder returned before the city listing fetch starts.
      if (!citySid || !cityDisplayName) return;
      const cityKey = `city:${cityDisplayName}`;
      if (findLocationFiredRef.current === cityKey) return;
      findLocationFiredRef.current = cityKey;
      plpAnalytics.trackFindLocation({
        content_names: [],
        content_category: '',
        store_city: cityDisplayName,
        intent_source: 'store_plp',
      });
      return;
    }

    if (isStoreWidgetLoading) return;

    const selectedStores = (
      selectedStoresData.length > 0
        ? selectedStoresData
        : selectedStoreData
          ? [selectedStoreData]
          : []
    )
      .map((store) => ({
        id: store.id ?? store.sid ?? '',
        name: store.storeName?.trim() ?? '',
        city: store.cityName?.trim() || cityDisplayName,
      }))
      .filter((store) => String(store.id).trim() !== '');

    if (selectedStores.length === 0) return;

    const selectionKey = `selected:${selectedStores.map((store) => store.id).join(',')}`;
    if (findLocationFiredRef.current === selectionKey) return;
    findLocationFiredRef.current = selectionKey;
    plpAnalytics.trackFindLocation({
      content_names: [],
      content_category: '',
      store_count: selectedStores.length,
      store_ids: JSON.stringify(selectedStores.map((store) => String(store.id))),
      store_names: JSON.stringify(selectedStores.map((store) => store.name.trim())),
      store_city: [...new Set(selectedStores.map((store) => store.city.trim()).filter(Boolean))].join(', '),
      city: selectedStores[0].city,
      intent_source: 'store_plp',
    });
  }, [
    plpAnalytics,
    plp.isLoading,
    plp.listingMeta,
    isCityPage,
    citySid,
    isStoreWidgetLoading,
    selectedStoreData,
    selectedStoresData,
    cityDisplayName,
  ]);

  const { metaPresentation, breadcrumbItems, storeCountLabel } = usePlpPresentation({
    listingMeta: plp.listingMeta,
    pathname,
    isStorePlp: true,
    isSearchPlp: false,
    storeCitySlug,
    cityDisplayName,
    storeAreaName,
  });

  const displayListingMeta = useMemo(() => {
    if (!plp.listingMeta) return null;
    if (!metaPresentation) return plp.listingMeta;
    return {
      ...plp.listingMeta,
      header: metaPresentation.headerLabel,
      ...(metaPresentation.filtersResetUrl && { filtersResetUrl: metaPresentation.filtersResetUrl }),
      ...(metaPresentation.exploreLinks?.length && { exploreLinks: metaPresentation.exploreLinks }),
      ...(metaPresentation.listingSeo && { listingSeo: metaPresentation.listingSeo }),
    };
  }, [plp.listingMeta, metaPresentation]);

  const {
    isOpen: isFilterSheetOpen,
    openFilterSheet,
    previewFilters,
    selectedOptions: filterSelectedOptions,
    applyUrl: filterApplyUrl,
    isLoadingPreview,
    toggleOption: handleToggleFilterOption,
    handleApply: handleApplyFilter,
    handleReset: handleResetFilter,
    handleClose: handleCloseFilter,
  } = usePlpFilters({
    listingFilters: plp.listingMeta?.filters ?? [],
    filtersResetUrl: fixedFiltersResetUrl,
    tags,
    filterParams,
    service: filtersService,
    navigate,
  });

  const {
    previewFilters: desktopPreviewFilters,
    selectedOptions: desktopSelectedOptions,
    isPendingNavigation: isDesktopFilterPending,
    toggleOption: handleDesktopToggleFilter,
    handleReset: handleDesktopResetFilter,
  } = usePlpDesktopFilters({
    listingFilters: plp.listingMeta?.filters ?? [],
    filtersResetUrl: fixedFiltersResetUrl,
    isLoading: plp.isLoading,
    tags,
    filterParams,
    service: filtersService,
    navigate,
  });

  usePlpRedirectUrlNavigation({ redirectUrl: plp.redirectUrl, sidFromUrl });

  const { videoCallTahItemsCount, maxVideoCallCount, message: videoCallMessage } = useVideoCallCart();

  const handleOpenFilter = useCallback(() => {
    plpAnalytics.trackFilterButtonClicked();
    openFilterSheet();
  }, [plpAnalytics, openFilterSheet]);

  const handleChangePincodeForStore = useCallback(() => {
    setPincodeDescription('Browse our exquisite designs: locate stores near you!');
    openPopup((nextSid) => {
      if (nextSid) {
        setIsNoStoreFound(false);
        const urlParams = new URLSearchParams(location.search);
        urlParams.set('sid', nextSid);
        navigate({ pathname: location.pathname, search: `?${urlParams.toString()}` });
      } else {
        setIsNoStoreFound(true);
      }
    });
  }, [openPopup, location.search, location.pathname, navigate]);

  const handleApplyFilterWithAnalytics = useCallback(() => {
    plpAnalytics.trackFilterApply(previewFilters, filterSelectedOptions);
    handleApplyFilter();
  }, [plpAnalytics, previewFilters, filterSelectedOptions, handleApplyFilter]);

  const handleResetFilterWithAnalytics = useCallback(() => {
    plpAnalytics.trackFilterReset(previewFilters, filterSelectedOptions);
    handleResetFilter();
  }, [plpAnalytics, previewFilters, filterSelectedOptions, handleResetFilter]);

  const handleCloseFilterWithAnalytics = useCallback(() => {
    plpAnalytics.trackFilterClose();
    handleCloseFilter();
  }, [plpAnalytics, handleCloseFilter]);

  const handleDesktopResetWithAnalytics = useCallback(() => {
    plpAnalytics.trackFilterReset(desktopPreviewFilters, desktopSelectedOptions);
    handleDesktopResetFilter();
  }, [plpAnalytics, desktopPreviewFilters, desktopSelectedOptions, handleDesktopResetFilter]);

  const plpCoreProps = {
    ...plp,
    listingMeta: displayListingMeta,
    countLabel: storeCountLabel,
    currentSortBy: sortBy,
    pincode,
    onOpenPincode: handleOpenBottomPincode,
    onOpenSort: handleOpenSortSheet,
    onOpenFilter: handleOpenFilter,
    onApplySort: handleApplySort,
    onSortHover: plpAnalytics.trackSortFilterHovered,
    openPdpInNewTab: isDesktop,
    isStoreDesignsActive: Boolean(sidFromUrl) || isNoStoreFound,
    isStorePlp: true as const,
    isStoreCitySelectionMode,
    selectedStoreData,
    selectedStoresData,
    isStoreWidgetLoading,
    storeCitySlug,
    cityToStoresMap,
    breadcrumbItems,
    hasStoreLocationWidget: false as const,
    isNoStoreFound,
    onChangePincodeForStore: handleChangePincodeForStore,
    onProductClick: plpAnalytics.trackProductClick,
    onWishlistClick: plpAnalytics.trackWishlistClick,
    onWishlistToggle: plpAnalytics.trackWishlistToggle,
    onVideoClick: plpAnalytics.trackVideoClick,
    onVideoCallCartAdd: plpAnalytics.trackVideoCallCartAdd,
    onViewDetailsClick: plpAnalytics.trackViewDetailsClick,
    onProductViewed: plpAnalytics.trackListingPageProductViewed,
    viewedProductIds: plpAnalytics.viewedProductIds,
    isDesktopPendingNavigation: isDesktopFilterPending,
  };

  const desktopFilterData = {
    shouldShow: plp.isLoading || desktopPreviewFilters.length > 0,
    filters: desktopPreviewFilters,
    selectedOptions: desktopSelectedOptions,
    filtersResetUrl: plp.listingMeta?.filtersResetUrl,
    isPendingNavigation: isDesktopFilterPending,
    isStorePlp: true as const,
    currentSortBy: sortBy,
    onToggleOption: handleDesktopToggleFilter,
    onReset: handleDesktopResetWithAnalytics,
    onFilterItemToggle: plpAnalytics.trackDesktopFilterItemSelected,
  };

  const filterSheetProps = {
    open: isFilterSheetOpen,
    filters: previewFilters,
    selectedOptions: filterSelectedOptions,
    applyUrl: filterApplyUrl,
    isLoadingPreview,
    filtersResetUrl: plp.listingMeta?.filtersResetUrl,
    onToggleOption: handleToggleFilterOption,
    onApply: handleApplyFilterWithAnalytics,
    onReset: handleResetFilterWithAnalytics,
    onClose: handleCloseFilterWithAnalytics,
    onFilterItemToggle: plpAnalytics.trackFilterItemSelected,
    onFilterCategoryExpand: plpAnalytics.trackFilterCategoryExpand,
  };

  const sortSheetProps = {
    open: isSortSheetOpen,
    currentSortBy: sortBy,
    onOpenChange: setIsSortSheetOpen,
    onApply: handleApplySortFromSheet,
    onSortOptionSelect: plpAnalytics.trackSortOptionSelect,
    onCancel: plpAnalytics.trackSortCancel,
  };

  const pincodeProps = {
    open: isPopupOpen,
    onOpenChange: handlePincodeOpenChange,
    inputValue,
    error: pincodeError,
    description: pincodeDescription,
    onChange: handleChange,
    onSubmit: handlePincodeSubmit,
  };

  const videoCallProps = {
    itemsCount: videoCallTahItemsCount,
    maxCount: maxVideoCallCount,
    message: videoCallMessage,
  };

  const showScrollToTop = isDesktop && !isTopVisible;

  const belowGridData = {
    shouldShow: cityToStoresMap != null,
    cityToStoresMap,
    selectedStoreData,
    selectedStoresData,
    isCityPage,
    cityDisplayName,
  };

  return (
    <>
      <div ref={topSentinelRef} aria-hidden />
      <PlpBase
        {...plpCoreProps}
        renderDesktopFilterSidebar={
          desktopFilterData.shouldShow
            ? () => (
                <PlpDesktopFilterSidebar
                  filters={desktopFilterData.filters}
                  selectedOptions={desktopFilterData.selectedOptions}
                  filtersResetUrl={desktopFilterData.filtersResetUrl}
                  isPendingNavigation={desktopFilterData.isPendingNavigation}
                  isStorePlp={desktopFilterData.isStorePlp}
                  currentSortBy={desktopFilterData.currentSortBy}
                  onToggleOption={desktopFilterData.onToggleOption}
                  onReset={desktopFilterData.onReset}
                  onFilterItemToggle={desktopFilterData.onFilterItemToggle}
                />
              )
            : undefined
        }
        renderBelowGrid={
          belowGridData.shouldShow
            ? () => (
                <>
                  <OtherStoresWidget
                    cityToStoresMap={belowGridData.cityToStoresMap}
                    selectedStoreData={belowGridData.selectedStoreData}
                    selectedStoresData={belowGridData.selectedStoresData}
                    isCityPage={belowGridData.isCityPage}
                    cityName={belowGridData.cityDisplayName}
                  />
                  {!belowGridData.isCityPage &&
                    belowGridData.selectedStoresData.length === 1 &&
                    belowGridData.selectedStoreData && (
                      <StoreFaqWidget
                        storeName={belowGridData.selectedStoreData.storeName ?? ''}
                        storeAddress={belowGridData.selectedStoreData.address ?? ''}
                        storePhone={belowGridData.selectedStoreData.contactNumber ?? ''}
                      />
                    )}
                </>
              )
            : undefined
        }
      />
      <PlpFilterSheet {...filterSheetProps} />
      <PlpSortSheet {...sortSheetProps} />
      <PincodePopup {...pincodeProps} />
      <VideoCallStickyBar {...videoCallProps} />
      <PlpScrollToTopButton show={showScrollToTop} />
    </>
  );
}
